import React from 'react'
import DefaultLayout, {
  LayoutSpacingOptions,
} from '@src/components/DefaultLayout'
import {
  makeStyles,
  Theme,
  Typography,
  Grid,
  Container,
  Button,
  FormControlLabel,
  RadioGroup,
} from '@material-ui/core/'
import { FormattedMessage, useIntl } from 'react-intl'
import messages from '@src/routes/SearchResults/messages'
import { Field, Form, Formik } from 'formik'
import vrStyles from '@src/styles/utils/vrStyles'
import LiquidInputAdapter from '@src/components/LiquidInputAdapter'
import clsx from 'clsx'
import { searchResultsRoute, useRouter } from '@src/routes'
import {
  getValidSearch,
  ProductSearchType,
  SearchFocusType,
} from '@src/utils/searchUtils'
import { SitePreference, useCurrentUser } from '@src/utils/useCurrentUser'
import * as yup from 'yup'
import RadioButton from '@src/components/RadioButton'
import { useFacetSearchQuery } from '@src/queries/FacetSearchQuery.generated'
import FacetSkeleton from './FacetSkeleton'
import { isServer } from '@src/utils/isServer'

const ChromatogramSearch = () => {
  const intl = useIntl()

  const useStyles = makeStyles((theme: Theme) => {
    const { vr2, vr4 } = vrStyles
    return {
      h1: { paddingBottom: '16px' },
      h2: {
        paddingBottom: '16px',
        letterSpacing: '1px',
        lineHeight: '24px',
        textTransform: 'uppercase',
      },
      button: {
        width: '100%',
        [theme.breakpoints.up('sm')]: {
          width: 'auto',
        },
      },
      vr2,
      vr4,
      formControlLabelRoot: {
        display: 'flex',
        alignItems: 'center',
        margin: '0 8px 0px 0',
      },
      searchField: {
        height: '50px',
        boxSizing: 'border-box',
        maxWidth: '620px',
        '& #term-help-text': {
          marginTop: '4px',
        },
        '& input': {
          fontSize: theme.typography.pxToRem(16),
          height: '36px',
          padding: '10px 20px',
          '&::placeholder': {
            color: theme.palette.grey[500],
            fontSize: theme.typography.pxToRem(14),
          },
        },
      },
      h3: {
        fontSize: theme.typography.pxToRem(14),
        lineHeight: '20px',
        marginBottom: '3px',
        '& label': { marginBottom: '2px' },
      },
      techniquesContainer: {
        minHeight: '235px',
      },
      techniques: {
        fontSize: theme.typography.pxToRem(14),
        lineHeight: '20px',
        marginBottom: '30px',
        '& label': {
          marginBottom: '5px',
          [theme.breakpoints.up('md')]: {
            marginBottom: '2px',
          },
        },
      },
      techniqueLabel: {
        fontSize: theme.typography.pxToRem(14),
      },
      formControlLabel: {
        margin: 0,
      },
      checkboxRoot: {
        padding: '0',
        margin: '0 8px 0px 0',
      },
      body: {
        [theme.breakpoints.up('md')]: {
          width: '1320px',
          margin: '0 auto',
          paddingLeft: '20px',
        },
      },
      container: {
        margin: 0,
        padding: '0px 16px',
        [theme.breakpoints.up('md')]: {
          padding: '0px',
        },
      },
      radioButton: {
        padding: '0 9px',
      },
    }
  })

  const classes = useStyles()
  const { getSitePreference } = useCurrentUser()
  const currentUserOrgId = getSitePreference(SitePreference.CatalogFilter)
  const router = useRouter()
  const isTest = process.env.NODE_ENV === 'test'
  const clientFetchPolicy = !isServer ? 'cache-first' : 'network-only'
  const fetchPolicy = isTest ? 'no-cache' : clientFetchPolicy

  const { data, loading } = useFacetSearchQuery({
    fetchPolicy,
    variables: {
      type: ProductSearchType.Protocol.toUpperCase() as any,
      facetSet: 'facet_web_titertest_app',
    },
  })

  const validationSchema = yup.object().shape({
    term: yup
      .string()
      .ensure()
      .when('technique', {
        is: (value) => !value,
        then: yup
          .string()
          .required(
            intl.formatMessage(messages.CHROMATOGRAM_SEARCH_VALIDATION_MSG)
          ),
      }),
  })
  const handleChange = (e, formikBag) => {
    formikBag.setFieldValue('technique', e.target.value)

    formikBag.setFieldTouched('technique', true)
    // clears validation error
    formikBag.setFieldTouched('term', false)
  }

  const handleSubmit = async (values, actions) => {
    const { term, technique } = values
    const hasTechnique = !!technique

    const selectedFacets = hasTechnique
      ? [`facet_web_titertest_app:${technique}`]
      : []

    if (term || hasTechnique) {
      const searchTerm = hasTechnique && !term ? 'facet-search' : term.trim()

      router.push(
        `${searchResultsRoute.searchResults(searchTerm)}${getValidSearch({
          term: searchTerm,
          focus: SearchFocusType.Chromatograms,
          type: ProductSearchType.Protocol,
          orgId: currentUserOrgId,
          selectedFacets,
        })}`
      )
    }

    actions.setSubmitting(false)
  }

  const handleReset = (formikBag) => {
    formikBag.resetForm()
  }

  return (
    <DefaultLayout className={classes.body} spacing={LayoutSpacingOptions.SM}>
      <Container className={classes.container} maxWidth="lg">
        <Grid item xs={12}>
          <Typography variant="h1" component="h1" className={classes.h1}>
            <FormattedMessage
              id="CHROMATOGRAM_SEARCH"
              defaultMessage="Chromatogram Search"
            />
          </Typography>
          <Typography variant="h2" component="h2" className={classes.h2}>
            <FormattedMessage {...messages.CHROMATOGRAM_SEARCH_PROMPT} />
          </Typography>
          <Formik
            initialValues={{
              term: '',
              technique: '',
            }}
            onSubmit={handleSubmit}
            validationSchema={validationSchema}
          >
            {(formikBag) => (
              <Form data-testid={'chromatograph-search-form'}>
                <div className={clsx(classes.vr2, classes.searchField)}>
                  <Field
                    name="term"
                    component={LiquidInputAdapter}
                    data-testid="chromatogram-search-term"
                    placeholder={intl.formatMessage(messages.SEARCH_BY_KEYWORD)}
                  />
                </div>
                <div className={classes.techniquesContainer}>
                  {loading && <FacetSkeleton></FacetSkeleton>}
                  {data?.getFacets?.facets?.length && (
                    <>
                      <h3 className={classes.h3}>
                        <FormattedMessage {...messages.TECHNIQUE} />
                      </h3>
                      <div className={classes.techniques}>
                        <RadioGroup
                          id="technique"
                          aria-label="focus-selection"
                          value={formikBag.values.technique}
                          onChange={(e) => {
                            handleChange(e, formikBag)
                          }}
                        >
                          {data?.getFacets?.facets[0].options.map(
                            (option, index) => (
                              <FormControlLabel
                                control={
                                  <RadioButton
                                    className={classes.radioButton}
                                  />
                                }
                                label={option.value}
                                data-testid={`chromatogram-search-technique-${index}`}
                                name="technique"
                                value={option.value}
                                checked={
                                  formikBag.values.technique === option.value
                                }
                                key={`technique-${index}`}
                              />
                            )
                          )}
                        </RadioGroup>
                      </div>
                    </>
                  )}
                </div>

                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="large"
                  className={classes.button}
                >
                  <FormattedMessage {...messages.SEARCH} />
                </Button>

                <Button
                  variant="text"
                  size="large"
                  onClick={() => handleReset(formikBag)}
                  className={classes.button}
                >
                  <FormattedMessage {...messages.RESET} />
                </Button>
              </Form>
            )}
          </Formik>
        </Grid>
      </Container>
    </DefaultLayout>
  )
}

export default ChromatogramSearch
